import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type ServiceUsage,
  useRevokeServiceKey,
  useRotateServiceKey,
  useSetServiceBudget,
  useSetServiceTier,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { formatRelativeTime } from "@/lib/format";
import { logger } from "@/lib/logger";
import {
  budgetOf,
  defaultBudget,
  formatAmount,
  OVERLAP_HOURS,
  parseBudget,
  SERVICE_LABEL,
} from "@/lib/services-gateway";

import { BudgetInput } from "./budget-input";
import { ConfirmButton } from "./confirm-button";

/** An admin's controls for one service: tier, budget, rotate, revoke. */
export function ManageServiceForm({
  projectId,
  usage,
  onClose,
}: {
  projectId: number;
  usage: ServiceUsage;
  onClose: () => void;
}) {
  const setTier = useSetServiceTier(projectId);
  const setBudget = useSetServiceBudget(projectId);
  const rotate = useRotateServiceKey(projectId);
  const revoke = useRevokeServiceKey(projectId);
  const [tier, setTierValue] = useState(usage.tier ?? "");
  const [amount, setAmount] = useState(() => {
    const current = budgetOf(usage);
    return current === null ? "" : String(current);
  });
  const [overlap, setOverlap] = useState("24");
  const busy = setTier.isPending || setBudget.isPending || rotate.isPending || revoke.isPending;
  // Empty means "back to the environment default".
  const budget = amount.trim() === "" ? defaultBudget(usage.unit) : parseBudget(usage.unit, amount);

  const rotateKey = async () => {
    try {
      const result = await rotate.mutateAsync({
        service: usage.service,
        overlapHours: Number(overlap),
      });
      toast.success(
        result?.redeployed
          ? "New key written. Dev is redeploying."
          : "New key written. Redeploy dev to use it.",
      );
    } catch (err) {
      logger.error("[services] rotate failed:", err);
      toast.error(readApiErrorMessage(err, "Could not rotate the key. Please try again."));
    }
  };

  const run = async (label: string, action: () => Promise<unknown>, success: string) => {
    try {
      await action();
      toast.success(success);
    } catch (err) {
      logger.error(`[services] ${label} failed:`, err);
      toast.error(readApiErrorMessage(err, `Could not ${label}. Please try again.`));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{SERVICE_LABEL[usage.service]}</DialogTitle>
        <DialogDescription>
          Limits apply from the next request; nothing is redeployed. Rotating writes a new key to
          the repository and redeploys dev. The budget can be at most{" "}
          {formatAmount(usage.unit, usage.max_monthly)} a month.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="space-y-5">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="svc-tier">Rate-limit tier (empty: the default)</Label>
              <Input
                id="svc-tier"
                value={tier}
                onChange={(e) => setTierValue(e.target.value)}
                placeholder="e.g. medium"
                maxLength={32}
              />
            </div>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() =>
                void run(
                  "set the tier",
                  () => setTier.mutateAsync({ service: usage.service, tier: tier.trim() || null }),
                  "Tier saved.",
                )
              }
            >
              Save tier
            </Button>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <BudgetInput
                unit={usage.unit}
                value={amount}
                onChange={setAmount}
                id="svc-budget"
                optional
              />
            </div>
            <Button
              variant="outline"
              disabled={busy || budget === null}
              onClick={() =>
                budget &&
                void run(
                  "set the budget",
                  () => setBudget.mutateAsync({ service: usage.service, budget }),
                  "Budget saved.",
                )
              }
            >
              Save budget
            </Button>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="svc-overlap">Old key keeps working for</Label>
              <Select value={overlap} onValueChange={setOverlap}>
                <SelectTrigger id="svc-overlap">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OVERLAP_HOURS.map((hours) => (
                    <SelectItem key={hours} value={String(hours)}>
                      {hours === 0 ? "No overlap (stops now)" : `${hours} hours`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ConfirmButton
              label="Rotate key"
              title="Rotate this key?"
              description={
                overlap === "0"
                  ? "The new key is written and dev redeploys. The old key stops working now, so the running app loses this service until the redeploy lands."
                  : `The new key is written and dev redeploys. The old key keeps working ${overlap} hours.`
              }
              confirmLabel="Rotate"
              onConfirm={() => void rotateKey()}
              disabled={busy}
              destructive={overlap === "0"}
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs tracking-wider uppercase">Keys</p>
            {(usage.keys ?? []).map((key) => (
              <div key={key.key_id} className="flex items-center justify-between gap-2 text-xs">
                <span>
                  <code className="bg-muted/40 rounded px-1 font-mono">{key.display_prefix}…</code>{" "}
                  {key.status === "revoked"
                    ? `revoked ${formatRelativeTime(key.revoked_at ?? null)}`
                    : key.not_after
                      ? `stops ${formatRelativeTime(key.not_after)}`
                      : "active"}
                </span>
                {key.status === "active" && (
                  <ConfirmButton
                    label="Revoke"
                    title="Revoke this key now?"
                    description="The app loses access to this service within about a minute, until it gets a new key (rotate). Use this when a key may have leaked."
                    confirmLabel="Revoke now"
                    onConfirm={() =>
                      void run(
                        "revoke the key",
                        () => revoke.mutateAsync({ keyId: key.key_id }),
                        "Key revoked. Rotate to give the app a new one.",
                      )
                    }
                    disabled={busy}
                    destructive
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}
